import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@scio3/ui';
import { ADMIN_ROLES, type AdminRole, type AdminUser } from '@scio3/shared-types';
import { STAFF_INVITE_ADMIN_USER } from '@scio3/graphql-client';
import { useDialogSubmitState } from '@/hooks/useDialogSubmitState';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface InviteResult {
  staffInviteAdminUser: AdminUser;
}

export function InviteUserDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AdminRole>(ADMIN_ROLES.ENGINEER);

  const [inviteMutation, { loading }] = useMutation<InviteResult>(STAFF_INVITE_ADMIN_USER, {
    refetchQueries: ['staffAdminUsers'],
  });

  const emailOk = /.+@optisigns\.com$/i.test(email.trim());
  const { disabled: submitDisabled, disabledTooltip } = useDialogSubmitState({
    isSubmitting: loading,
    isRequiredFilled: !!email.trim(),
    validationError: email.trim() && !emailOk ? t('Must be an @optisigns.com address') : '',
  });

  const handleSubmit = async () => {
    try {
      await inviteMutation({ variables: { email: email.trim(), role } });
      toast.success(t('Invite sent'), {
        description: t('User will have access on their next @optisigns.com sign-in.'),
      });
      setEmail('');
      setRole(ADMIN_ROLES.ENGINEER);
      onOpenChange(false);
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? t('Unknown error');
      // Backend throws ConflictException if email already exists.
      toast.error(t('Invite failed'), { description: msg });
    }
  };

  const resetOnClose = (next: boolean) => {
    if (!next) {
      setEmail('');
      setRole(ADMIN_ROLES.ENGINEER);
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={resetOnClose}>
      <DialogContent data-testid="invite-user-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('Invite user')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="invite-email">{t('Email')}</Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="first.last@optisigns.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="invite-email-input"
              autoComplete="off"
            />
          </div>

          <div className="space-y-2">
            <Label>{t('Role')}</Label>
            <RadioGroup
              value={role}
              onValueChange={(v) => setRole(v as AdminRole)}
              className="grid grid-cols-2 gap-2"
            >
              <RoleOption value={ADMIN_ROLES.ROOT} label={t('Root')} />
              <RoleOption value={ADMIN_ROLES.ENGINEER} label={t('Engineer')} />
              <RoleOption value={ADMIN_ROLES.SUPPORT} label={t('Support')} />
              <RoleOption value={ADMIN_ROLES.READONLY} label={t('Read only')} />
            </RadioGroup>
          </div>

          <p className="text-sm text-muted-foreground">
            {t('They will get access on first Google sign-in.')}
          </p>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{t('Cancel')}</Button>
          </DialogClose>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={submitDisabled ? 0 : -1}>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitDisabled}
                    data-testid="invite-user-submit"
                  >
                    {loading ? t('Sending…') : t('Send invite')}
                  </Button>
                </span>
              </TooltipTrigger>
              {disabledTooltip ? <TooltipContent>{disabledTooltip}</TooltipContent> : null}
            </Tooltip>
          </TooltipProvider>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RoleOption({ value, label }: { value: AdminRole; label: string }) {
  const id = `role-${value}`;
  return (
    <div className="flex items-center gap-2 rounded-md border px-3 py-2">
      <RadioGroupItem id={id} value={value} />
      <Label htmlFor={id} className="cursor-pointer">
        {label}
      </Label>
    </div>
  );
}
